import { useState, useEffect, useRef } from 'react';
import { Gift, Plus, Trash2, Calendar, X, Upload, Download, Loader2, Tag, Pencil, Check, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

export default function AdminHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('General');
  const [saving, setSaving] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [newCatName, setNewCatName] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);
  const fileRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hRes, cRes] = await Promise.all([
        adminAPI.getHolidays(),
        adminAPI.getHolidayCategories()
      ]);
      setHolidays(hRes.data);
      const cats = cRes.data || [];
      setCategories(cats);
      if (cats.length > 0 && !category) setCategory(cats[0].name);
    } catch {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const catNames = categories.map(c => c.name);
  const filtered = selectedCat === 'all' ? holidays : holidays.filter(h => h.category === selectedCat);

  const handleCreate = async () => {
    if (!name || !date) { toast.error('Name and date are required'); return; }
    setSaving(true);
    try {
      await adminAPI.createHoliday({ name, date, description: desc, category });
      toast.success('Holiday added');
      setShowAdd(false);
      setName(''); setDate(''); setDesc(''); setCategory(catNames[0] || 'General');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteHoliday(id);
      toast.success('Holiday deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCSVFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvContent(reader.result);
    reader.readAsText(file);
  };

  const handleCSVImport = async () => {
    if (!csvContent) { toast.error('Upload a CSV file first'); return; }
    setCsvLoading(true);
    try {
      const res = await adminAPI.importHolidaysCSV(csvContent);
      setCsvResult(res.data);
      toast.success(`${res.data.created} holidays imported`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCsvLoading(false);
    }
  };

  const holidayCSVTemplate = `Name,Date,Description,Category
Republic Day,2026-01-26,National holiday,General
Founder's Day,2026-03-15,Company celebration,Head Office
`;

  const downloadHolidayTemplate = () => {
    try {
      const blob = new Blob([holidayCSVTemplate], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'holiday_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      const encoded = encodeURIComponent(holidayCSVTemplate);
      window.open('data:text/csv;charset=utf-8,' + encoded);
    }
  };

  const handleAddCategory = async () => {
    const trimmed = newCatName.trim();
    if (!trimmed) { toast.error('Enter a category name'); return; }
    setCatSaving(true);
    try {
      await adminAPI.createHolidayCategory(trimmed);
      toast.success(`Category "${trimmed}" created`);
      setNewCatName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setCatSaving(false);
    }
  };

  const handleRenameCategory = async (catId) => {
    const trimmed = editCatName.trim();
    if (!trimmed) { toast.error('Enter a name'); return; }
    setCatSaving(true);
    try {
      await adminAPI.renameHolidayCategory(catId, trimmed);
      toast.success('Category renamed');
      setEditingCatId(null);
      setEditCatName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = async (catId, catName) => {
    try {
      await adminAPI.deleteHolidayCategory(catId);
      toast.success(`Category "${catName}" deleted`);
      setConfirmDeleteCat(null);
      if (selectedCat === catName) setSelectedCat('all');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const catColors = ['bg-[#026536]/10 text-[#026536]', 'bg-[#dea439]/10 text-[#dea439]', 'bg-blue-50 text-blue-600', 'bg-purple-50 text-purple-600', 'bg-pink-50 text-pink-600'];

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 data-testid="admin-holidays-title" className="text-2xl font-extrabold text-[#1a2529]">Holidays</h1>
            <p className="text-sm text-[#1a2529]/50">{holidays.length} holidays across {categories.length} categories</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="manage-categories-btn" onClick={() => setShowCatManager(!showCatManager)} size="sm" variant="outline" className="gap-1.5 font-semibold">
              <FolderPlus size={14} /> Categories
            </Button>
            <Button data-testid="add-holiday-btn" onClick={() => setShowAdd(true)} size="sm" className="bg-[#dea439] hover:bg-[#dea439]/90 gap-1.5 font-semibold">
              <Plus size={14} /> Add
            </Button>
            <Button data-testid="csv-holiday-btn" onClick={() => setShowCSV(true)} size="sm" variant="outline" className="gap-1.5 font-semibold">
              <Upload size={14} /> CSV Import
            </Button>
          </div>
        </div>

        {/* Category Manager */}
        {showCatManager && (
          <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1a2529] flex items-center gap-2"><Tag size={16} className="text-[#026536]" /> Manage Categories</h3>
              <button data-testid="close-cat-manager" onClick={() => setShowCatManager(false)}><X size={18} className="text-[#1a2529]/40" /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <Input data-testid="new-category-name-input" value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" className="h-9 flex-1"
                onKeyDown={e => e.key === 'Enter' && handleAddCategory()} />
              <Button data-testid="add-category-btn" onClick={handleAddCategory} disabled={catSaving} size="sm" className="bg-[#026536] hover:bg-[#026536]/90 gap-1 font-semibold h-9">
                {catSaving ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />} Add
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {categories.map((cat, idx) => {
                const hCount = holidays.filter(h => h.category === cat.name).length;
                const isEditing = editingCatId === cat.id;
                return (
                  <div key={cat.id} data-testid={`cat-item-${idx}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#f4f4f5] group">
                    {isEditing ? (
                      <>
                        <Input data-testid={`cat-rename-input-${idx}`} value={editCatName} onChange={e => setEditCatName(e.target.value)} className="h-8 flex-1 text-sm"
                          onKeyDown={e => e.key === 'Enter' && handleRenameCategory(cat.id)} autoFocus />
                        <button data-testid={`cat-rename-save-${idx}`} onClick={() => handleRenameCategory(cat.id)} className="h-7 w-7 rounded flex items-center justify-center bg-[#026536] text-white hover:bg-[#026536]/90">
                          <Check size={12} />
                        </button>
                        <button onClick={() => { setEditingCatId(null); setEditCatName(''); }} className="h-7 w-7 rounded flex items-center justify-center hover:bg-white text-[#1a2529]/40">
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <>
                        <Badge className={`${catColors[idx % catColors.length]} border-0 text-xs`}>{cat.name}</Badge>
                        <span className="text-[11px] text-[#1a2529]/40 ml-auto">{hCount} holidays</span>
                        {confirmDeleteCat === cat.id ? (
                          <>
                            <span className="text-[10px] text-red-500 font-medium">Delete?</span>
                            <button data-testid={`cat-confirm-delete-${idx}`} onClick={() => handleDeleteCategory(cat.id, cat.name)}
                              className="h-7 px-2 rounded bg-red-500 text-white text-[10px] font-semibold hover:bg-red-600">
                              Yes
                            </button>
                            <button onClick={() => setConfirmDeleteCat(null)}
                              className="h-7 px-2 rounded bg-[#e5e5e5] text-[#1a2529] text-[10px] font-semibold hover:bg-[#d4d4d4]">
                              No
                            </button>
                          </>
                        ) : (
                          <>
                            <button data-testid={`cat-rename-btn-${idx}`} onClick={() => { setEditingCatId(cat.id); setEditCatName(cat.name); }}
                              className="h-7 w-7 rounded flex items-center justify-center hover:bg-white text-[#1a2529]/40 hover:text-[#026536]">
                              <Pencil size={12} />
                            </button>
                            {cat.name !== 'General' && (
                              <button data-testid={`cat-delete-btn-${idx}`} onClick={() => setConfirmDeleteCat(cat.id)}
                                className="h-7 w-7 rounded flex items-center justify-center hover:bg-red-50 text-red-400 hover:text-red-500">
                                <Trash2 size={12} />
                              </button>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {categories.length === 0 && <p className="text-xs text-[#1a2529]/40 text-center py-3">No categories yet</p>}
            </div>
          </div>
        )}

        {/* Add Holiday */}
        {showAdd && (
          <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1a2529]">Add Holiday</h3>
              <button onClick={() => setShowAdd(false)}><X size={18} className="text-[#1a2529]/40" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Holiday Name *</Label>
                <Input data-testid="holiday-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Diwali" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Date *</Label>
                <Input data-testid="holiday-date-input" type="date" value={date} onChange={e => setDate(e.target.value)} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Category</Label>
                <select data-testid="holiday-category-select" value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                  {catNames.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-[#1a2529]/70">Description</Label>
                <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional" className="h-10 mt-1" />
              </div>
            </div>
            <Button data-testid="save-holiday-btn" onClick={handleCreate} disabled={saving} className="mt-4 bg-[#dea439] hover:bg-[#dea439]/90 font-semibold gap-2">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Add Holiday
            </Button>
          </div>
        )}

        {/* CSV Import */}
        {showCSV && (
          <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1a2529]">Import Holidays via CSV</h3>
              <button onClick={() => { setShowCSV(false); setCsvContent(''); setCsvResult(null); }}><X size={18} className="text-[#1a2529]/40" /></button>
            </div>
            <button data-testid="download-holiday-template" onClick={downloadHolidayTemplate} className="text-xs text-[#026536] font-semibold hover:underline mb-3 flex items-center gap-1">
              <Download size={12} /> Download Template
            </button>
            <div className="border-2 border-dashed border-border/40 rounded-xl p-4 text-center">
              <input data-testid="holiday-csv-input" type="file" accept=".csv" onChange={handleCSVFile} ref={fileRef} className="hidden" />
              <button onClick={() => fileRef.current?.click()} className="text-sm text-[#1a2529]/50 font-medium">
                {csvContent ? `File loaded (${csvContent.split('\n').length - 1} rows)` : 'Click to select CSV file'}
              </button>
            </div>
            {csvResult && (
              <div data-testid="holiday-csv-result" className="mt-3 bg-[#f4f4f5] rounded-xl p-3 text-xs space-y-1">
                <p className="font-semibold text-[#026536]">Created: {csvResult.created}</p>
                {csvResult.errors?.map((e, i) => <p key={i} className="text-red-500">{e}</p>)}
              </div>
            )}
            <Button data-testid="import-holiday-csv" onClick={handleCSVImport} disabled={csvLoading || !csvContent} className="mt-3 bg-[#dea439] hover:bg-[#dea439]/90 font-semibold gap-2">
              {csvLoading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Import
            </Button>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2">
          <button data-testid="cat-all" onClick={() => setSelectedCat('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${selectedCat === 'all' ? 'bg-[#1a2529] text-white' : 'bg-white text-[#1a2529]/60 hover:bg-[#f4f4f5]'}`}>
            All ({holidays.length})
          </button>
          {categories.map((c, i) => {
            const count = holidays.filter(h => h.category === c.name).length;
            return (
              <button key={c.id} data-testid={`cat-${c.name}`} onClick={() => setSelectedCat(c.name)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${selectedCat === c.name ? 'bg-[#1a2529] text-white' : 'bg-white text-[#1a2529]/60 hover:bg-[#f4f4f5]'}`}>
                <Tag size={10} /> {c.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Holiday List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 border-2 border-[#dea439] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <Gift className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
              <p className="font-medium text-[#1a2529]/50">No holidays found</p>
            </div>
          ) : filtered.map((h, idx) => {
            const isPast = new Date(h.date) < new Date(new Date().toDateString());
            const catIdx = catNames.indexOf(h.category || 'General');
            const dt = new Date(h.date);
            const dd = String(dt.getDate()).padStart(2, '0');
            const mm = String(dt.getMonth() + 1).padStart(2, '0');
            const yy = String(dt.getFullYear()).slice(-2);
            return (
              <div key={h.id} data-testid={`holiday-${idx}`}
                className={`bg-white rounded-xl p-4 shadow-sm border border-border/30 flex items-center gap-3 ${isPast ? 'opacity-50' : ''}`}>
                <div className="h-12 w-12 rounded-xl bg-[#dea439]/10 flex flex-col items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-[#dea439] leading-none">{dt.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}</span>
                  <span className="text-base font-extrabold text-[#1a2529] leading-none mt-0.5">{dt.getDate()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1a2529] truncate">{h.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-[#1a2529]/30 font-medium">
                      {`${dd}/${mm}/${yy}`} &middot; {dt.toLocaleDateString('en-IN', { weekday: 'long' })}
                    </span>
                    <Badge className={`${catColors[catIdx >= 0 ? catIdx % catColors.length : 0]} border-0 text-[10px]`}>{h.category || 'General'}</Badge>
                  </div>
                  {h.description && <p className="text-[11px] text-[#1a2529]/40 mt-0.5 truncate">{h.description}</p>}
                </div>
                <button data-testid={`delete-holiday-${idx}`} onClick={() => handleDelete(h.id)}
                  className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-red-50 text-[#1a2529]/20 hover:text-red-500 transition-colors shrink-0">
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
